/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Popover, Typography } from '@mui/material';
import React from 'react';
import { CustomIconButton, dateUtils } from '@opengeoweb/shared';
import { Animation } from '@opengeoweb/theme';
import { AnimationLengthMenu } from './AnimationLengthMenu';
import type {
  AnimationLengthSetting,
  RelativeTimeOffset,
} from './animationLengthUtils';
import {
  activeOffsets,
  AnimationLengthMode,
  modeTranslationKey,
  shortUnitTranslationKey,
} from './animationLengthUtils';
import { useTimesliderTranslation } from '../../../utils/i18n';
import { minutesToDescribedDuration } from '../../TimeSlider/timeSliderUtils';

export interface AnimationLengthPanelProps {
  setting: AnimationLengthSetting;
  onChange: (setting: AnimationLengthSetting, isStep?: boolean) => void;
  onInputChange?: () => void;
  animationStartTime?: string;
  animationEndTime?: string;
  autoTimeStepLayerName?: string;
  timeStep?: number;
  isAutoDisabled?: boolean;
  disabled?: boolean;
  referenceTimeFromLayer?: string;
}

const styles = {
  button: {
    padding: '0px',
    paddingRight: '12px',
    width: '100%!important',
    minWidth: '64px',
    justifyContent: 'space-between',
  },
  buttonLabel: {
    fontSize: 12,
    fontWeight: 400,
    textTransform: 'none',
  },
};

export const asDisplayTime = (time?: string): string =>
  time
    ? `${dateUtils.dateToString(dateUtils.utc(time), dateUtils.DATE_FORMAT_DATEPICKER)} UTC`
    : '';

export const AnimationLengthPanel: React.FC<AnimationLengthPanelProps> = ({
  setting,
  onChange,
  onInputChange,
  animationStartTime,
  animationEndTime,
  autoTimeStepLayerName,
  timeStep,
  isAutoDisabled = false,
  disabled = false,
  referenceTimeFromLayer,
}) => {
  const { t } = useTimesliderTranslation();
  const [anchorRef, setAnchorRef] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState(false);

  const describedAnimationLength =
    animationStartTime && animationEndTime
      ? minutesToDescribedDuration(
          dateUtils.differenceInMinutes(
            dateUtils.utc(animationEndTime),
            dateUtils.utc(animationStartTime),
          ),
        )
      : '';

  /* Zero has no direction, so it carries no sign. */
  const asSignedOffsetLabel = ({ value, unit }: RelativeTimeOffset): string => {
    const sign = value === 0 ? '' : `${value < 0 ? '−' : '+'} `;
    return `${sign}${Math.abs(value)} ${t(shortUnitTranslationKey[unit])}`;
  };

  const { startOffset, endOffset } = activeOffsets(setting);
  const offsetsLabel = `${asSignedOffsetLabel(
    startOffset,
  )} / ${asSignedOffsetLabel(endOffset)}`;

  const buttonLabel = ((): string => {
    switch (setting.mode) {
      case AnimationLengthMode.Auto:
        return `${t('timeslider-auto')} ${describedAnimationLength}`;
      case AnimationLengthMode.Now:
        return `${t('timeslider-now')} ${offsetsLabel}`;
      case AnimationLengthMode.RecurringPeriod:
        return `${setting.recurringPeriod.anchorTime} ${offsetsLabel}`;
      case AnimationLengthMode.ReferenceTime:
        return `Ref. ${offsetsLabel}`;
      case AnimationLengthMode.Exact:
        return t('timeslider-animation-length-exact');
      default:
        return describedAnimationLength;
    }
  })();

  /* The offsets are counted from an anchor, so the heading names which one. */
  const tooltipHeading = ((): string => {
    switch (setting.mode) {
      case AnimationLengthMode.Now:
        return `${t('timeslider-now')} - ${dateUtils.dateToString(dateUtils.utc(), dateUtils.DATE_FORMAT_HOURS)}`;
      case AnimationLengthMode.RecurringPeriod:
        return `${t('timeslider-recurring-period')} - ${setting.recurringPeriod.anchorTime}`;
      case AnimationLengthMode.ReferenceTime:
        return `Ref. - ${asDisplayTime(referenceTimeFromLayer)}`;
      default:
        return t(modeTranslationKey[setting.mode]);
    }
  })();

  const tooltipTitle = (
    <Box>
      <Typography variant="body2">{tooltipHeading}</Typography>
      <Typography variant="body2">
        {`${t('timeslider-start-time')} ${asDisplayTime(animationStartTime)}`}
      </Typography>
      <Typography variant="body2">
        {`${t('timeslider-end-time')} ${asDisplayTime(animationEndTime)}`}
      </Typography>
    </Box>
  );

  return (
    <Box
      onKeyDown={(event): void => {
        event.stopPropagation();
      }}
    >
      <CustomIconButton
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('timeslider-animation-length')}
        data-testid="animationLengthPanelButton"
        isSelected
        onClick={(): void => setOpen(true)}
        ref={setAnchorRef}
        sx={styles.button}
        tooltipProps={{ title: tooltipTitle, placement: 'top' }}
        variant="tool"
      >
        <Animation />
        <Typography sx={styles.buttonLabel}>{buttonLabel}</Typography>
      </CustomIconButton>
      {/* The panel holds form controls, so it is a dialog: a Menu would swallow Tab and the arrow keys. */}
      {!!anchorRef && (
        <Popover
          anchorEl={anchorRef}
          anchorOrigin={{ vertical: -15, horizontal: 'center' }}
          onClose={(): void => setOpen(false)}
          open={open}
          slotProps={{
            backdrop: { sx: { backgroundColor: 'transparent' } },
            paper: {
              role: 'dialog',
              'aria-label': t('timeslider-animation-length'),
            },
          }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <AnimationLengthMenu
            animationEndTime={animationEndTime}
            animationStartTime={animationStartTime}
            autoTimeStepLayerName={autoTimeStepLayerName}
            disabled={disabled}
            isAutoDisabled={isAutoDisabled}
            onChange={onChange}
            onInputChange={onInputChange}
            referenceTimeFromLayer={referenceTimeFromLayer}
            setting={setting}
            timeStep={timeStep}
          />
        </Popover>
      )}
    </Box>
  );
};
